import { useCallback, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useEditClassifierDrawer } from './useEditClassifierDrawer';
import { EditField } from '@dealstack/frontend/shared/components/Documents/FormFields/EditField';
import {
	Classifier,
	ClassifierFields,
} from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/types';
import { DealClassifierTct, UpdateDealClassifierDto } from '@dealstack/shared/lib/generated';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DrawerSubmission } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/DrawerSubmission';
import {
	archiveField,
	nameField,
	parentIdField,
} from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/fields';
import {
	ClassifierForm,
	DrawFieldContainer,
	FieldWrapper,
} from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/styled';
import { createClassifiersOptions } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/utils';
import { Text } from '@mantine/core';
import { getClassificationParent } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/EditClassifierDrawer/utils';

interface Props {
	classifier: Classifier;
	handleOnCancel: () => void;
	availableClassifiers?: DealClassifierTct[];
}

export const DrawerBodyEdit = ({ classifier, handleOnCancel, availableClassifiers }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const { formState, onSave, isLoading } = useEditClassifierDrawer(classifier);

	const onCancel = useCallback(() => {
		formState.reset();
		handleOnCancel();
	}, [handleOnCancel, formState]);

	const options = useMemo(
		() => createClassifiersOptions(availableClassifiers, classifier),
		[availableClassifiers, classifier]
	);

	// do not allow a root classification with children to be assigned under another classification
	const isParentFieldDisabled = Boolean(
		!classifier.classifierParentId && classifier?.children?.length
	);

	const parentClassifier = getClassificationParent(classifier, availableClassifiers);

	// do not allow child classifier to be enabled if its parent classifier is disabled
	const isArchiveFieldDisabled = parentClassifier?.deleted === true;

	const formFields = useMemo(
		() => [
			nameField(tAdminDashboard),
			parentIdField(tAdminDashboard, options, isParentFieldDisabled),
			archiveField(tAdminDashboard, isArchiveFieldDisabled),
		],
		[tAdminDashboard, options, isParentFieldDisabled, isArchiveFieldDisabled]
	);

	return (
		<DrawFieldContainer>
			<FormProvider<UpdateDealClassifierDto> {...formState}>
				<ClassifierForm>
					{formFields.map((field) => (
						<FieldWrapper key={`${field.fieldKey}_${field.label} `}>
							<EditField field={field} control={formState.control} />
							{field.fieldKey === ClassifierFields.classifierParentId &&
								isParentFieldDisabled && (
									<Text>
										{tAdminDashboard(
											'drawer.field.placeHolder.re-assign-child-classifications'
										)}
									</Text>
								)}
						</FieldWrapper>
					))}
					{isLoading ? (
						<InlineLoader />
					) : (
						<DrawerSubmission onCancel={onCancel} onSave={onSave} />
					)}
				</ClassifierForm>
			</FormProvider>
		</DrawFieldContainer>
	);
};
